import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, StatusPill } from '@careandkey/shared';
import { supabase } from '../lib/supabase';
import { Layout, Topbar } from '../components/Layout';

type Portfolio = { portfolio_id: string; name: string; management_company_party_id: string; property_count: number; billed_count: number };
type Relation = { id: string; property_id: string; rights: { can_see?: boolean; can_validate_quotes?: boolean; is_billed?: boolean } | null };
type Prop = { id: string; name: string };

const FIELD = 'h-9 w-full rounded-md border border-border bg-[#F7F8FA] px-3 text-sm text-fg outline-none';

export function ManagementCompanies() {
  const { t } = useTranslation();
  const [rows, setRows] = useState<Portfolio[]>([]);
  const [props, setProps] = useState<Prop[]>([]);
  const [open, setOpen] = useState<string | null>(null);
  const [rel, setRel] = useState<Record<string, Relation[]>>({});
  const [newName, setNewName] = useState('');
  const [csv, setCsv] = useState('');
  const [report, setReport] = useState<{ line: string; status: string }[] | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const [p, pr] = await Promise.all([
      supabase.from('portfolio_overview').select('*').order('name'),
      supabase.from('properties').select('id,name').is('deleted_at', null).order('name'),
    ]);
    setRows((p.data as Portfolio[]) ?? []);
    setProps((pr.data as Prop[]) ?? []);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  async function loadRelations(pf: string) {
    const r = await supabase.from('property_relations').select('id,property_id,rights').eq('portfolio_id', pf).eq('relation', 'managed_by').is('deleted_at', null);
    setRel((m) => ({ ...m, [pf]: (r.data as Relation[]) ?? [] }));
  }
  async function toggleOpen(pf: string) {
    if (open === pf) { setOpen(null); return; }
    setOpen(pf); setReport(null);
    if (!rel[pf]) await loadRelations(pf);
  }
  async function createCompany() {
    if (!newName.trim()) return;
    const org = (await supabase.from('organizations').select('id').limit(1).single()).data?.id;
    await supabase.rpc('create_management_company', { p_org: org, p_name: newName.trim() });
    setNewName(''); load();
  }
  async function assign(pf: string, propId: string) {
    await supabase.rpc('assign_property_portfolio', { p_portfolio: pf, p_property: propId });
    await loadRelations(pf); load();
  }
  async function unassign(pf: string, relId: string) {
    await supabase.rpc('unassign_property_portfolio', { p_relation: relId });
    await loadRelations(pf); load();
  }
  async function setRight(pf: string, relId: string, cur: Relation['rights'], key: string, val: boolean) {
    await supabase.rpc('set_property_relation_rights', { p_relation: relId, p_rights: { ...cur, [key]: val } });
    await loadRelations(pf);
  }
  // CSV import: one property NAME per line → assign; per-line report incl. duplicates.
  async function importCsv(pf: string) {
    const lines = csv.split('\n').map((l) => l.trim()).filter(Boolean);
    const out: { line: string; status: string }[] = [];
    for (const line of lines) {
      const match = props.find((p) => p.name.toLowerCase() === line.toLowerCase());
      if (!match) { out.push({ line, status: 'not_found' }); continue; }
      const r = await supabase.rpc('assign_property_portfolio', { p_portfolio: pf, p_property: match.id });
      if (r.error) out.push({ line, status: (r.error as any).code === 'CK001' ? 'duplicate' : 'error' });
      else out.push({ line, status: 'ok' });
    }
    setReport(out); setCsv(''); await loadRelations(pf); load();
  }

  const nameOf = (id: string) => props.find((p) => p.id === id)?.name ?? id.slice(0, 8);

  return (
    <Layout header={<Topbar />}>
      <div className="px-7 pt-6"><h1 className="text-[30px] font-semibold tracking-[-0.02em]">{t('nav.managementCompanies')}</h1></div>
      <div className="flex flex-col gap-[18px] px-7 pb-7 pt-[18px]">
        <Card className="flex items-end gap-2 p-4">
          <label className="flex-1 text-sm"><span className="mb-1 block text-muted">{t('mgmt.newCompany')}</span>
            <input className={FIELD} value={newName} onChange={(e) => setNewName(e.target.value)} placeholder={t('mgmt.companyName')} /></label>
          <Button onClick={createCompany}>+ {t('mgmt.create')}</Button>
        </Card>

        {loading ? <Card className="p-6 text-muted">{t('common.loading')}</Card>
        : rows.length === 0 ? <Card className="p-6 text-muted">{t('common.noData')}</Card>
        : rows.map((pf) => (
          <Card key={pf.portfolio_id} className="p-0">
            <button className="flex w-full items-center justify-between px-5 py-4 text-left" onClick={() => toggleOpen(pf.portfolio_id)}>
              <span className="font-medium">{pf.name}</span>
              <span className="flex items-center gap-3 text-sm text-muted">
                <StatusPill tone="open">{t('mgmt.properties', { n: pf.property_count })}</StatusPill>
                <StatusPill tone="done">{t('mgmt.billed', { n: pf.billed_count })}</StatusPill>
                <span>{open === pf.portfolio_id ? '▾' : '▸'}</span>
              </span>
            </button>
            {open === pf.portfolio_id && (
              <div className="border-t border-divider p-5">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-xs text-muted">
                    <th className="py-2">{t('nav.properties')}</th><th>{t('mgmt.canSee')}</th><th>{t('mgmt.canValidate')}</th><th>{t('mgmt.isBilled')}</th><th />
                  </tr></thead>
                  <tbody>
                    {(rel[pf.portfolio_id] ?? []).map((r) => (
                      <tr key={r.id} className="border-t border-divider">
                        <td className="py-2 font-medium">{nameOf(r.property_id)}</td>
                        {(['can_see', 'can_validate_quotes', 'is_billed'] as const).map((k) => (
                          <td key={k}><input type="checkbox" checked={!!r.rights?.[k]} onChange={(e) => setRight(pf.portfolio_id, r.id, r.rights ?? {}, k, e.target.checked)} /></td>
                        ))}
                        <td className="text-right"><Button variant="ghost" size="sm" onClick={() => unassign(pf.portfolio_id, r.id)}>{t('mgmt.remove')}</Button></td>
                      </tr>
                    ))}
                    {(rel[pf.portfolio_id] ?? []).length === 0 && <tr><td colSpan={5} className="py-3 text-muted">{t('common.noData')}</td></tr>}
                  </tbody>
                </table>
                <div className="mt-4 flex items-end gap-2">
                  <label className="flex-1 text-sm"><span className="mb-1 block text-muted">{t('mgmt.assignProperty')}</span>
                    <select className={FIELD} defaultValue="" onChange={(e) => { if (e.target.value) { assign(pf.portfolio_id, e.target.value); e.target.value = ''; } }}>
                      <option value="">{t('mgmt.selectProperty')}</option>
                      {props.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select></label>
                </div>
                <div className="mt-4">
                  <span className="mb-1 block text-xs text-muted">{t('mgmt.csvImport')}</span>
                  <textarea className="h-24 w-full rounded-md border border-border bg-[#F7F8FA] p-2 font-mono text-xs" value={csv} onChange={(e) => setCsv(e.target.value)} placeholder={t('mgmt.csvHint')} />
                  <Button className="mt-2" size="sm" onClick={() => importCsv(pf.portfolio_id)}>{t('mgmt.import')}</Button>
                  {report && (
                    <div className="mt-3 rounded-md border border-border p-2 text-xs">
                      {report.map((r, i) => (
                        <div key={i} className="flex justify-between py-0.5">
                          <span>{r.line}</span>
                          <span className={r.status === 'ok' ? 'text-st-done-fg' : r.status === 'duplicate' ? 'text-st-overdue-fg' : 'text-muted'}>{t('mgmt.csv.' + r.status)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </Card>
        ))}
      </div>
    </Layout>
  );
}
